import { useMemo, useState } from 'react';

import type { PlatformDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

import { hasValidContent } from '../utils/contentValidation';
import type { PlatformGroupedPosts, PlatformType } from '../utils/platformGrouping';
import { AVAILABLE_PLATFORMS } from '../utils/platformGrouping';

function groupPostsByPlatform(platformData: PlatformDataMap): PlatformGroupedPosts {
	return {
		facebook: platformData.FACEBOOK?.items || [],
		instagram: platformData.INSTAGRAM?.items || [],
		reddit: platformData.REDDIT?.items || [],
		facebookgroups: platformData.FACEBOOKGROUPS?.items || [],
	};
}

function groupLoadingStatesByPlatform(platformData: PlatformDataMap): Record<PlatformType, boolean> {
	return {
		facebook: platformData.FACEBOOK?.status === 'PENDING',
		instagram: platformData.INSTAGRAM?.status === 'PENDING',
		reddit: platformData.REDDIT?.status === 'PENDING',
		facebookgroups: platformData.FACEBOOKGROUPS?.status === 'PENDING',
	};
}

function filterPosts(
	filteredPlatformPosts: Partial<PlatformGroupedPosts>,
	loadingStates: Record<PlatformType, boolean>,
) {
	return Object.entries(filteredPlatformPosts).reduce<FilteredPostsAccumulator>(
		(acc, [platform, posts]) => {
			const platformType = platform as PlatformType;
			const isLoadingPlatform = loadingStates[platformType];
			const shouldShowPlatform = posts.length > 0 || isLoadingPlatform;

			if (!shouldShowPlatform) return acc;

			const regularPosts = posts
				.filter((post) => post.postTypeName.toLowerCase() !== 'story')
				.filter((post) => hasValidContent(post, false));
			const stories = posts
				.filter((post) => post.postTypeName.toLowerCase() === 'story')
				.filter((post) => hasValidContent(post, true));

			// Add regular posts
			regularPosts.forEach((post) => {
				acc.allPosts.push({ platform: platformType, post });
			});

			// Add stories
			stories.forEach((post) => {
				acc.allStories.push({ platform: platformType, post });
			});

			// Add skeleton if no content but loading
			if (posts.length === 0 && isLoadingPlatform) {
				acc.skeletons.push({ platform: platformType });
			}

			return acc;
		},
		{ allPosts: [], allStories: [], skeletons: [] },
	);
}

type LoadedPostsAccumulator = {
	platform: PlatformType;
	post: ReadyToSharePostWithPlatform;
};

type SkeletonAccumulator = { platform: PlatformType };

type FilteredPostsAccumulator = {
	allPosts: LoadedPostsAccumulator[];
	allStories: LoadedPostsAccumulator[];
	skeletons: SkeletonAccumulator[];
};

type UsePlatformFilterResult = ModelHookResult<
	{
		selectedPlatform: PlatformType | undefined;
		allPosts: LoadedPostsAccumulator[];
		allStories: LoadedPostsAccumulator[];
		skeletons: SkeletonAccumulator[];
		hasAnyValidContent: boolean;
		availablePlatforms: PlatformType[];
	},
	{
		selectPlatform: (platform: PlatformType) => void;
		clearPlatformSelection: () => void;
	}
>;

function computeAvailablePlatforms(
	groupedPosts: PlatformGroupedPosts,
	loadingStates: Record<PlatformType, boolean>,
): PlatformType[] {
	const allGroupedPostsFiltered = filterPosts(groupedPosts, loadingStates);
	const platforms = new Set<PlatformType>();
	allGroupedPostsFiltered.allPosts.forEach((item) => platforms.add(item.platform));
	allGroupedPostsFiltered.allStories.forEach((item) => platforms.add(item.platform));
	allGroupedPostsFiltered.skeletons.forEach((item) => platforms.add(item.platform));
	// Filter to only include platforms that should be shown in chips (excludes facebookgroups)
	return Array.from(platforms).filter((p) => AVAILABLE_PLATFORMS.includes(p));
}

export function usePlatformFilter(platformData: PlatformDataMap): UsePlatformFilterResult {
	// State management for selected platform (null means show all platforms)
	const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | undefined>(undefined);

	const groupedPosts: PlatformGroupedPosts = useMemo(() => {
		return groupPostsByPlatform(platformData);
	}, [platformData]);

	// Transform loading states from context
	const loadingStates: Record<PlatformType, boolean> = useMemo(() => {
		return groupLoadingStatesByPlatform(platformData);
	}, [platformData]);

	const selectPlatform = (platform: PlatformType) => {
		setSelectedPlatform(platform);
	};

	const clearPlatformSelection = () => {
		setSelectedPlatform(undefined);
	};

	const filteredPlatformPosts = useMemo((): Partial<PlatformGroupedPosts> => {
		if (selectedPlatform) {
			// When Facebook is selected, include both facebook and facebookgroups
			if (selectedPlatform === 'facebook') {
				return {
					facebook: groupedPosts.facebook,
					facebookgroups: groupedPosts.facebookgroups,
				};
			}
			// For other platforms, show only that platform
			return { [selectedPlatform]: groupedPosts[selectedPlatform] };
		}
		return groupedPosts;
	}, [groupedPosts, selectedPlatform]);

	const filteredPosts = useMemo(() => {
		return filterPosts(filteredPlatformPosts, loadingStates);
	}, [filteredPlatformPosts, loadingStates]);

	// Compute available platforms from ALL grouped posts (not filtered by selection)
	const availablePlatforms = useMemo(() => {
		return computeAvailablePlatforms(groupedPosts, loadingStates);
	}, [groupedPosts, loadingStates]);

	const hasAnyValidContent = filteredPosts.allPosts.length > 0 || filteredPosts.allStories.length > 0;

	return {
		data: {
			selectedPlatform,
			...filteredPosts,
			hasAnyValidContent,
			availablePlatforms,
		},
		actions: {
			selectPlatform,
			clearPlatformSelection,
		},
	};
}
